
interface Package {
  id: string;
  name: string;
  price: number;
  maxPrice: number;
  targetReturn: string;
  timeline: string;
  description: string;
  features: string[];
  investments: string[];
  benefits: string[];
  ideal: string;
  popular?: boolean;
}

interface PackageComparisonProps {
  packages: Package[];
}

export default function PackageComparison({ packages }: PackageComparisonProps) {
  const comparisonFeatures = [
    'Minimum Investment',
    'Maximum Investment', 
    'Target Returns',
    'Investment Timeline',
    'Number of Projects',
    'Progress Reports',
    'Investor Portal',
    'Support Level',
    'Site Visits',
    'Account Manager',
    'Early Access',
    'Exit Consultation'
  ];

  const getFeatureValue = (pkg: Package, feature: string): string => {
    switch (feature) {
      case 'Minimum Investment':
        return `$${pkg.price.toLocaleString()}`;
      case 'Maximum Investment':
        return `$${pkg.maxPrice.toLocaleString()}`;
      case 'Target Returns':
        return pkg.targetReturn;
      case 'Investment Timeline':
        return pkg.timeline;
      case 'Number of Projects':
        return pkg.id === 'starter' ? '2-3' : pkg.id === 'growth' ? '4-6' : 'All';
      case 'Progress Reports':
        return pkg.id === 'starter' ? 'Quarterly' : pkg.id === 'growth' ? 'Monthly' : 'Weekly';
      case 'Investor Portal':
        return pkg.id === 'starter' ? 'Basic' : pkg.id === 'growth' ? 'Premium' : 'VIP';
      case 'Support Level':
        return pkg.id === 'starter' ? 'Email' : pkg.id === 'growth' ? 'Priority' : 'Dedicated';
      case 'Site Visits':
        return pkg.id === 'starter' ? '✗' : pkg.id === 'growth' ? 'Available' : 'Exclusive';
      case 'Account Manager':
        return pkg.id === 'premium' ? '✓' : '✗';
      case 'Early Access':
        return pkg.id === 'premium' ? '✓' : '✗';
      case 'Exit Consultation':
        return pkg.id === 'premium' ? '✓' : '✗';
      default:
        return '✓';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Package Comparison</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Compare features across all investment packages to find the perfect fit for your investment goals.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                {packages.map((pkg) => (
                  <th key={pkg.id} className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    <div className="flex flex-col items-center">
                      <span className="text-lg font-bold">{pkg.name}</span>
                      {pkg.popular && (
                        <span className="bg-emerald-500 text-white px-2 py-1 rounded-full text-xs mt-1">
                          Popular
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comparisonFeatures.map((feature, index) => (
                <tr key={feature} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{feature}</td>
                  {packages.map((pkg) => (
                    <td key={pkg.id} className="px-6 py-4 text-center text-sm text-gray-700">
                      <span className={getFeatureValue(pkg, feature) === '✓' ? 'text-emerald-600' : 
                                     getFeatureValue(pkg, feature) === '✗' ? 'text-gray-400' : ''}>
                        {getFeatureValue(pkg, feature)}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Still have questions about which package is right for you?
          </p>
          <button className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
            Schedule a Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
